import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { X, Spinner, ShieldCheck } from "@phosphor-icons/react";
import { brl, errMsg } from "@/lib/api";
import { CheckoutForm } from "./CheckoutForm";
import { PixPanel } from "./PixPanel";
import { PaymentSuccess } from "./PaymentSuccess";

export const CheckoutSheet = ({ open, onClose, productName, price, acceptPix = true, acceptCard = true, onStart, pollStatus, onMockConfirm }) => {
  const [phase, setPhase] = useState("form");
  const [form, setForm] = useState({ name: "", email: "" });
  const [method, setMethod] = useState(acceptPix ? "pix" : "card");
  const [loading, setLoading] = useState(false);
  const [pix, setPix] = useState(null);
  const [done, setDone] = useState(null);

  useEffect(() => { setMethod(acceptPix ? "pix" : "card"); }, [acceptPix, acceptCard]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const approve = (r) => { setDone({ ...r, buyer_name: r.buyer_name || form.name }); setPhase("success"); };

  useEffect(() => {
    if (!open || phase !== "pix" || !pix) return undefined;
    const id = setInterval(async () => {
      try {
        const r = await pollStatus(pix.tx_id);
        if (r.status === "approved") approve(r);
      } catch (e) { console.warn("poll", e?.message); }
    }, 3000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, phase, pix, pollStatus]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await onStart({ name: form.name.trim(), email: form.email.trim().toLowerCase(), method });
      if (r.status === "approved") return approve(r);
      if (r.checkout_url) { setPhase("redirecting"); window.location.assign(r.checkout_url); return; }
      setPix({ ...r, amount: r.amount ?? price });
      setPhase("pix");
    } catch (err) {
      toast.error(errMsg(err, "Não foi possível iniciar o pagamento"));
    } finally { setLoading(false); }
  };

  const checkNow = async () => {
    setLoading(true);
    try {
      const r = await pollStatus(pix.tx_id);
      if (r.status === "approved") approve(r);
      else toast.info("Ainda não identificamos o pagamento. Aguarde alguns segundos.");
    } catch (err) { toast.error(errMsg(err, "Erro ao verificar")); }
    finally { setLoading(false); }
  };

  const simulate = async () => {
    setLoading(true);
    try { approve(await onMockConfirm(pix.tx_id)); }
    catch (err) { toast.error(errMsg(err, "Erro ao simular")); }
    finally { setLoading(false); }
  };

  const canClose = phase !== "success" && phase !== "redirecting";

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center sm:p-6"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-black/75 backdrop-blur-md" onClick={() => canClose && onClose()} />
          <motion.div
            role="dialog"
            aria-modal="true"
            data-testid="public-checkout-modal"
            initial={{ y: 60, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 32 }}
            className="vb-scroll-lock relative w-full sm:max-w-[480px] max-h-[94dvh] overflow-y-auto rounded-t-[28px] sm:rounded-[28px] bg-[#121824] border border-white/10 shadow-[0_-20px_80px_rgba(0,0,0,0.6)] text-[#F8FAFC]"
          >
            <div className="sm:hidden mx-auto mt-3 w-10 h-1.5 rounded-full bg-white/15" />
            <div className="px-5 sm:px-7 pt-4 sm:pt-6 pb-6 sm:pb-7">
              {phase !== "success" && (
                <div className="flex items-start justify-between gap-3 mb-5">
                  <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400 flex items-center gap-1.5"><ShieldCheck size={13} className="text-emerald-400" /> Checkout seguro</div>
                    <div className="font-display font-bold text-lg leading-tight mt-1 truncate" data-testid="checkout-product-name">{productName}</div>
                    <div className="font-display font-black text-[#FFB30F] text-xl tabular-nums" data-testid="checkout-price">{brl(price)}</div>
                  </div>
                  {canClose && (
                    <button type="button" onClick={onClose} className="shrink-0 w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors" aria-label="Fechar" data-testid="checkout-close-btn">
                      <X size={18} />
                    </button>
                  )}
                </div>
              )}

              {phase === "form" && (
                <CheckoutForm form={form} setForm={setForm} method={method} setMethod={setMethod}
                  acceptPix={acceptPix} acceptCard={acceptCard} price={price} loading={loading} onSubmit={submit} />
              )}
              {phase === "pix" && pix && (
                <PixPanel pix={pix} loading={loading} onCheckNow={checkNow} onBack={() => setPhase("form")} onSimulate={onMockConfirm ? simulate : null} />
              )}
              {phase === "redirecting" && (
                <div className="py-10 text-center" data-testid="checkout-redirecting">
                  <Spinner size={40} className="animate-spin mx-auto text-[#FFB30F]" />
                  <div className="font-display font-bold text-lg mt-4">Abrindo pagamento seguro…</div>
                  <div className="text-sm text-slate-400 mt-1">Você será levado ao checkout de cartão.</div>
                </div>
              )}
              {phase === "success" && done && <PaymentSuccess redirectUrl={done.redirect_url} buyerName={done.buyer_name} />}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};
